import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {StateReconciler} from 'redux-persist/lib/types';
import {CopilotWidget} from '../bootstrap/bootstrap.types';
import {copilotSettingsDefaults} from '../copilot.config';
import {ChatType, ChatStatus} from '../copilot.enums';
import {ChatItem, CopilotSettings, CopilotState} from '../copilot.types';
import {PlanCollapseState, PlanStatus} from '../plan/plan.enums';
import {PlanState, PlanStepState} from '../plan/plan.types';
import chatState from '../utils/creators/chatState';

const initialState: CopilotState = {
  chats: [],
  activeChatId: '',
  settings: copilotSettingsDefaults,
  isOpen: false,
  isConversationStartersOverlayVisible: false,
};

function getChatById(state: CopilotState, chatId: string) {
  return state.chats.find((chat) => chat.id === chatId);
}

const copilotSlice = createSlice({
  name: 'copilot',
  initialState: initialState,
  reducers: {
    createChat: (
      state,
      action: PayloadAction<{chatId: string; type: ChatType; isPlanModeEnabled: boolean}>,
    ) => {
      state.chats.push(chatState(action.payload));
      state.activeChatId = action.payload.chatId;
    },
    deleteChat: (state, action: PayloadAction<{chatId: string}>) => {
      const {chatId} = action.payload;
      state.chats = state.chats.filter((chat) => chat.id !== chatId);

      if (state.activeChatId === chatId) {
        state.activeChatId = state.chats.at(-1)?.id ?? '';
      }
    },
    setActiveChatId: (state, action: PayloadAction<string>) => {
      state.activeChatId = action.payload;
    },
    setChatTitle: (state, action: PayloadAction<{chatId: string; title: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.title = action.payload.title;
      }
    },
    updateSettings: (state, action: PayloadAction<Partial<CopilotSettings>>) => {
      state.settings = {...state.settings, ...action.payload};
    },
    toggleConversationStartersOverlay: (state, action: PayloadAction<boolean>) => {
      state.isConversationStartersOverlayVisible = action.payload;
    },
    setCopilotIsOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;

      // TODO: Consider moving this logic out of Redux - handle overlay visibility in thunks layer
      if (!state.isOpen) {
        state.isConversationStartersOverlayVisible = false;
      }
    },
    addChatItem: (state, action: PayloadAction<{chatId: string; chatItem: ChatItem}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.chatItems.push(action.payload.chatItem);
      }
    },
    clearChat: (state, action: PayloadAction<{chatId: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (!chat) {
        return;
      }

      const emptyChat = chatState({
        chatId: action.payload.chatId,
        type: chat.type,
        isPlanModeEnabled: chat.isPlanModeEnabled,
      });
      const chatItemsToPreserve = chat.chatItems.slice(0, 1);

      const clearedChat = {
        ...emptyChat,
        chatItems: chatItemsToPreserve,
        draftMessage: chat.draftMessage,
        isPlanModeToggleOn: chat.isPlanModeToggleOn,
      };

      state.chats = state.chats.map((chat) =>
        chat.id === action.payload.chatId ? clearedChat : chat,
      );
    },
    setInformationText: (
      state,
      action: PayloadAction<{chatId: string; informationText: string}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.informationText = action.payload.informationText;
      }
    },
    resetInformationText: (state, action: PayloadAction<{chatId: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.informationText = '';
      }
    },
    setDraftText: (state, action: PayloadAction<{chatId: string; text: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.draftMessage.text = action.payload.text;
      }
    },
    addFileIdsToDraft: (state, action: PayloadAction<{chatId: string; fileIds: string[]}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.draftMessage.fileIds.push(...action.payload.fileIds);
      }
    },
    removeFileIdFromDraft: (state, action: PayloadAction<{chatId: string; fileId: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.draftMessage.fileIds = chat.draftMessage.fileIds.filter(
          (fileId) => fileId !== action.payload.fileId,
        );
      }
    },
    clearDraft: (state, action: PayloadAction<{chatId: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.draftMessage = {
          text: '',
          fileIds: [],
        };
      }
    },
    setChatStatus: (state, action: PayloadAction<{chatId: string; status: ChatStatus}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.status = action.payload.status;
      }
    },
    setModalWidget: (
      state,
      action: PayloadAction<{chatId: string; modalWidget: CopilotWidget | null}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.modalWidget = action.payload.modalWidget;
      }
    },
    setBlockingChatItemId: (
      state,
      action: PayloadAction<{chatId: string; blockingChatItemId: string | null}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.blockingChatItemId = action.payload.blockingChatItemId;
      }
    },
    setPlan: (state, action: PayloadAction<{chatId: string; plan: PlanState | null}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.plan = action.payload.plan;
      }
    },
    clearPlan: (state, action: PayloadAction<{chatId: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.plan = null;
        chat.planCollapseState = PlanCollapseState.Expanded;
      }
    },
    updatePlanStep: (
      state,
      action: PayloadAction<{chatId: string; stepId: string; updates: Partial<PlanStepState>}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat && chat.plan) {
        chat.plan.steps = chat.plan.steps.map((step) =>
          step.id === action.payload.stepId ? {...step, ...action.payload.updates} : step,
        );
      }
    },
    updatePlanStatus: (state, action: PayloadAction<{chatId: string; status: PlanStatus}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat && chat.plan) {
        chat.plan.status = action.payload.status;
      }
    },
    setIsPlanModeToggleOn: (
      state,
      action: PayloadAction<{chatId: string; isPlanModeToggleOn: boolean}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.isPlanModeToggleOn = action.payload.isPlanModeToggleOn;
      }
    },
    togglePlanMode: (state, action: PayloadAction<{chatId: string}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.isPlanModeToggleOn = !chat.isPlanModeToggleOn;
      }
    },
    setPlanCollapseState: (
      state,
      action: PayloadAction<{chatId: string; planCollapseState: PlanCollapseState}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.planCollapseState = action.payload.planCollapseState;
      }
    },
    setContextWindowUsage: (state, action: PayloadAction<{chatId: string; tokens: number}>) => {
      const chat = getChatById(state, action.payload.chatId);

      if (chat) {
        chat.contextWindowUsage = action.payload.tokens;
      }
    },
    updateChatItem: (
      state,
      action: PayloadAction<{chatId: string; chatItemId: string; update: Partial<ChatItem>}>,
    ) => {
      const chat = getChatById(state, action.payload.chatId);

      if (!chat) {
        return;
      }

      const chatItem = chat.chatItems.find((item) => item.id === action.payload.chatItemId);

      if (chatItem) {
        Object.assign(chatItem, action.payload.update);
      }
    },
  },
});

function mergeCopilotState(
  inboundState: Partial<CopilotState>,
  originalState: CopilotState,
): CopilotState {
  return {
    ...originalState,
    ...inboundState,
    settings: {
      ...copilotSettingsDefaults,
      ...(inboundState.settings ?? {}),
      skillSearchConfig: {
        ...copilotSettingsDefaults.skillSearchConfig,
        ...(inboundState.settings?.skillSearchConfig ?? {}),
      },
    },
  };
}

const persistConfig = {
  key: 'copilot',
  storage,
  whitelist: ['settings', 'isOpen'], // only these parts will be persisted
  stateReconciler: mergeCopilotState as StateReconciler<CopilotState>,
};

const persistedReducer = persistReducer(persistConfig, copilotSlice.reducer);

export const {
  createChat,
  deleteChat,
  setActiveChatId,
  setChatTitle,
  updateSettings,
  toggleConversationStartersOverlay,
  setCopilotIsOpen,
  addChatItem,
  clearChat,
  setInformationText,
  resetInformationText,
  setDraftText,
  addFileIdsToDraft,
  removeFileIdFromDraft,
  clearDraft,
  setChatStatus,
  setModalWidget,
  setBlockingChatItemId,
  setPlan,
  clearPlan,
  updatePlanStep,
  updatePlanStatus,
  setIsPlanModeToggleOn,
  togglePlanMode,
  setPlanCollapseState,
  setContextWindowUsage,
  updateChatItem,
} = copilotSlice.actions;

export function selectChatById(state: {copilot: CopilotState}, chatId: string) {
  return getChatById(state.copilot, chatId);
}

export default persistedReducer;
